import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../api";
import { money } from "../calc";
import { generateServiceReceiptPDF } from "../pdf";
import ServiceClients from "./ServiceClients";
import FiscalSettings from "./FiscalSettings";
import ServiceClientReport from "./ServiceClientReport";
import { NfseActions } from "./NfseActions";

export const SERVICE_TYPES = ["Telemedicina", "Plantão médico / enfermagem", "Atendimento domiciliar (home care)", "Remoção para convênio / hospital", "Consulta / exame", "Treinamento / curso", "Outro"];
const STATUS = [{ v: "pendente", l: "A receber" }, { v: "pago", l: "Pago" }, { v: "cancelado", l: "Cancelado" }];
const PAY = ["PIX", "Transferência", "Boleto", "Cartão", "Dinheiro", "Faturado ao convênio"];
const emptyForm = () => ({ clientId: "", type: SERVICE_TYPES[0], description: "", date: new Date().toISOString().slice(0, 10), revenue: "", cost: "", status: "pendente", paymentMethod: "", notes: "", manualInvoiceNumber: "" });
const pill = (s) => ({ pendente: ["#fff4dc", "#8a5a00"], pago: ["#e7f7ee", "#0a6f3c"], cancelado: ["#fdeaea", "#b53b3b"] }[s] || ["#eef2f6", "#516b82"]);
const fmtDate = (d) => (d ? new Date(d + "T12:00:00").toLocaleDateString("pt-BR") : "—");

function Summary({ services }) {
  const month = new Date().toISOString().slice(0, 7);
  const ms = services.filter((s) => s.status !== "cancelado" && String(s.date).slice(0, 7) === month);
  const sum = (arr, k) => arr.reduce((t, s) => t + Number(s[k] || 0), 0);
  const items = [["Faturado no mês", sum(ms, "revenue")], ["Recebido", sum(ms.filter((s) => s.status === "pago"), "revenue")], ["A receber", sum(ms.filter((s) => s.status === "pendente"), "revenue")], ["Lucro estimado", sum(ms, "revenue") - sum(ms, "cost")]];
  return (
    <div className="report-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", marginBottom: 16 }}>
      {items.map(([l, v], i) => <div className="report-card" key={l} data-testid={`svc-summary-${i}`}><span>{l}</span><strong>{money(v)}</strong></div>)}
    </div>
  );
}

function ServiceForm({ form, setForm, clients, onSave, onCancel, editing, loading }) {
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <article className="card" data-testid="svc-form" style={{ marginBottom: 16 }}>
      <h2>{editing ? "Editar serviço" : "Novo serviço prestado"}</h2>
      <div className="module-grid two-col">
        <label className="full-label">Cliente (tomador)<select value={form.clientId} onChange={set("clientId")} data-testid="svc-client"><option value="">Selecione</option>{clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></label>
        <label className="full-label">Tipo de serviço<select value={form.type} onChange={set("type")} data-testid="svc-type">{SERVICE_TYPES.map((t) => <option key={t}>{t}</option>)}</select></label>
        <label className="full-label">Data da prestação<input type="date" value={form.date} onChange={set("date")} data-testid="svc-date" /></label>
        <label className="full-label">Valor cobrado (R$)<input type="number" step="0.01" value={form.revenue} onChange={set("revenue")} data-testid="svc-revenue" placeholder="0,00" /></label>
        <label className="full-label">Custos do serviço (R$)<input type="number" step="0.01" value={form.cost} onChange={set("cost")} data-testid="svc-cost" placeholder="Equipe, deslocamento, materiais" /></label>
        <label className="full-label">Situação<select value={form.status} onChange={set("status")} data-testid="svc-status">{STATUS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}</select></label>
        <label className="full-label">Forma de pagamento<select value={form.paymentMethod} onChange={set("paymentMethod")} data-testid="svc-payment"><option value="">—</option>{PAY.map((p) => <option key={p}>{p}</option>)}</select></label>
        <label className="full-label">Nº da nota emitida fora do sistema (opcional)<input value={form.manualInvoiceNumber} onChange={set("manualInvoiceNumber")} data-testid="svc-manual-nf" /></label>
      </div>
      <label className="full-label">Descrição (vai no recibo e na nota fiscal)<textarea rows={2} value={form.description} onChange={set("description")} data-testid="svc-description" placeholder="Ex.: Plantão de enfermagem 12h no Hospital X em 10/11" /></label>
      <label className="full-label">Observações internas<input value={form.notes} onChange={set("notes")} data-testid="svc-notes" /></label>
      <div className="final-price" style={{ marginTop: 6 }}><span>Lucro estimado: <strong>{money(Number(form.revenue || 0) - Number(form.cost || 0))}</strong></span></div>
      <div style={{ marginTop: 12 }}>
        <button className="primary" onClick={onSave} disabled={loading} data-testid="svc-save">{loading ? "Salvando…" : "Salvar serviço"}</button>
        <button className="secondary" style={{ marginLeft: 8 }} onClick={onCancel} data-testid="svc-cancel">Cancelar</button>
      </div>
    </article>
  );
}

export default function ServicosTab({ settings, role }) {
  const [view, setView] = useState("servicos");
  const [services, setServices] = useState([]);
  const [clients, setClients] = useState([]);
  const [fiscal, setFiscal] = useState(null);
  const [form, setForm] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("todos");

  const load = () => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/service-clients").then((r) => setClients(r.data)).catch(() => {});
    api.get("/fiscal/settings").then((r) => setFiscal(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const clientById = useMemo(() => Object.fromEntries(clients.map((c) => [c.id, c])), [clients]);
  const updateOne = (s) => setServices((list) => list.map((x) => (x.id === s.id ? s : x)));

  const openEdit = (s) => { setForm({ ...emptyForm(), ...Object.fromEntries(Object.keys(emptyForm()).map((k) => [k, s[k] ?? ""])) }); setEditingId(s.id); };
  const save = async () => {
    if (!form.clientId) return toast.error("Selecione o cliente. Cadastre-o na aba Clientes, se necessário.");
    if (!form.date) return toast.error("Informe a data.");
    setLoading(true);
    const payload = { ...form, revenue: Number(form.revenue || 0), cost: Number(form.cost || 0) };
    try {
      if (editingId) await api.patch(`/services/${editingId}`, payload); else await api.post("/services", payload);
      toast.success(editingId ? "Serviço atualizado." : "Serviço lançado."); setForm(null); setEditingId(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setLoading(false); }
  };
  const markPaid = async (s) => { try { updateOne((await api.patch(`/services/${s.id}`, { status: "pago" })).data); toast.success("Marcado como pago."); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const remove = async (s) => { if (!window.confirm("Excluir este serviço?")) return; try { await api.delete(`/services/${s.id}`); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const shown = services.filter((s) => filter === "todos" || s.status === filter);

  return (
    <section className="panel active">
      <div className="module-head">
        <div><h1>Serviços</h1><p>Telemedicina, plantões, atendimentos para hospitais e convênios. Os valores entram no Financeiro do mês.</p></div>
        {view === "servicos" && !form && <button className="primary" onClick={() => { setForm(emptyForm()); setEditingId(null); }} data-testid="svc-new">＋ Novo serviço</button>}
      </div>
      <div className="svc-tabs" role="tablist">
        {[["servicos", "Serviços prestados"], ["relatorio", "Por cliente"], ["clientes", "Clientes"], ["fiscal", "Nota fiscal"]].map(([id, l]) => (
          <button key={id} className={view === id ? "active" : ""} onClick={() => setView(id)} data-testid={`svc-view-${id}`}>{l}</button>
        ))}
      </div>
      {view === "relatorio" && <ServiceClientReport services={services} settings={settings} />}
      {view === "clientes" && <ServiceClients clients={clients} reload={load} />}
      {view === "fiscal" && <FiscalSettings fiscal={fiscal} setFiscal={setFiscal} canEdit={role === "admin"} />}
      {view === "servicos" && (
        <>
          <Summary services={services} />
          {form && <ServiceForm {...{ form, setForm, clients, onSave: save, onCancel: () => { setForm(null); setEditingId(null); }, editing: !!editingId, loading }} />}
          <article className="card">
            <div className="card-head"><h2>Serviços lançados</h2>
              <select value={filter} onChange={(e) => setFilter(e.target.value)} style={{ maxWidth: 180 }} data-testid="svc-filter"><option value="todos">Todos</option>{STATUS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}</select>
            </div>
            {shown.length === 0 ? <p className="hint">Nenhum serviço lançado. Cadastre o cliente em "Clientes" e depois clique em "Novo serviço".</p> : (
              <div className="module-list">
                {shown.map((s) => (
                  <div className="list-card" key={s.id} data-testid={`svc-${s.id}`}>
                    <div>
                      <strong>{s.type} <span className="status-pill" style={{ background: pill(s.status)[0], color: pill(s.status)[1], marginLeft: 6 }} data-testid={`svc-status-${s.id}`}>{STATUS.find((x) => x.v === s.status)?.l}</span></strong>
                      <span>{s.clientName || "Sem cliente"} • {fmtDate(s.date)} • Recibo nº {s.receiptNumber}</span>
                      <small>Valor {money(s.revenue)} • Custo {money(s.cost)} • Lucro {money(Number(s.revenue || 0) - Number(s.cost || 0))}{s.manualInvoiceNumber ? ` • NF ${s.manualInvoiceNumber}` : ""}</small>
                      <NfseActions s={s} fiscal={fiscal} onChange={updateOne} reload={load} />
                    </div>
                    <div className="list-value">
                      <strong>{money(s.revenue)}</strong>
                      {s.status === "pendente" && <button onClick={() => markPaid(s)} data-testid={`svc-paid-${s.id}`}>Marcar pago</button>}
                      <button onClick={() => generateServiceReceiptPDF(s, clientById[s.clientId], settings)} data-testid={`svc-receipt-${s.id}`}>Recibo</button>
                      <button onClick={() => openEdit(s)} data-testid={`svc-edit-${s.id}`}>Editar</button>
                      <button className="danger-lite" onClick={() => remove(s)} data-testid={`svc-delete-${s.id}`}>Excluir</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </article>
        </>
      )}
    </section>
  );
}
